import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import Rails from '@rails/ujs';

type Props = {
  modalId: string,
  instrument: InstrumentOption|null,
  optionType: 'put'|'call',
  strikePrice: string,
  optionExpiry: string,
  subscription: Subscription,
  subscriptionPosition: SubscriptionPosition,
  isInTradingHours: boolean,
};

type Subscription = {
  title: string,
  uid: string,
  allocatedUsd: number,
};
type SubscriptionPosition = {
  uid: string,
};
type InstrumentOption = {
  value: string,
  label: string,
  tradingLot: number,
};

type Quote = {
  currentPrice: string|null,
  currentPriceAt: Date|null,
};

const getTimeInJST = (date: Date|null): string => {
  if (!date) return "";
  return date.toLocaleTimeString('en-US', {
    timeZone: 'Asia/Tokyo',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false  // Use 24-hour format
  });
}

const ManualExitOrderForm: React.FC<Props> = ({
  modalId,
  instrument,
  optionType,
  strikePrice,
  optionExpiry,
  subscription,
  subscriptionPosition,
  isInTradingHours,
}) => {
  const [quantity, setQuantity] = useState<number|null>(instrument?.tradingLot || null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [quantityMethod, setQuantityMethod] = useState<string>('auto'); // ['auto', 'manual'
  const [quote, setQuote] = useState<Quote|null>(null);
  const [hasShown, setHasShown] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);

  if (!isInTradingHours) {
    return(
      <div className="alert alert-warning mb-0">
        現在取引時間外のため、取引を行うことができません。
      </div>
    );
  }

  if (!instrument) {
    return(
      <div className="alert alert-warning mb-0">
        銘柄情報が存在しないため、取引を行うことができません。
      </div>
    );
  }

  const updateQuote = async () => {
    try {
      const response = await axios.request({
        url: "/user/manual_entry_orders/quote",
        method: 'get',
        params: {
          instrument_ticker: instrument?.value,
        }
      });
      const quote: Quote = {
        currentPrice: response.data.current_price,
        currentPriceAt: (
          response.data.current_price_at ? new Date(response.data.current_price_at*1000) : null
        ),
      };
      setQuote(quote);
    } catch (error) {
      setQuote(null);
    }
  };

  const canSubmit = (
    !!instrument && !isSubmitting && !!subscription // && !!quote?.currentPrice
  );

  useEffect(() => {
    if (!instrument) return;
    if (!hasShown) return;
    updateQuote();
  }, [instrument, hasShown]);

  useEffect(() => {
    const modalElement = document.getElementById(modalId);
    if (!modalElement) return;
    modalElement.addEventListener('show.bs.modal', () => {
      setHasShown(true);
    });
  }, [modalId]);

  return(<>
    <div className="row">
      <div className="col-12 col-lg-6">
        <label>戦略</label>
        <input type="text" className="form-control" readOnly
          value={`${subscription.title}（$${(subscription.allocatedUsd)}割当済み）`}
        />
      </div>
      <div className="col-12 col-lg-6 mt-1 mt-lg-0">
        <label className="mt-1">取引銘柄</label>
        <input type="text" className="form-control" readOnly
          value={instrument.label}
        />
      </div>
    </div>
    <div className="row mt-1">
      <div className="col-6 col-lg-4">
        <label className="">オプション種別</label>
        <input type="text" className="form-control" readOnly
          value={optionType}
        />
      </div>
      <div className="col-6 col-lg-4">
        <label className="">権利行使価格</label>
        <input type="text" className="form-control" readOnly
          value={strikePrice}
        />
      </div>
      <div className="col-12 col-lg-4 mt-1 mt-lg-0">
        <label className="">満期日</label>
        <input type="text" className="form-control" readOnly
          value={optionExpiry}
        />
      </div>
    </div>
    <label className="mt-1">取引数量</label>
    <div className="manual-order-modal__radio-set d-flex">
      <div className="form-check">
        <label className="form-check-label">
          <input className="form-check-input" type="radio"
            checked={quantityMethod === 'auto'}
            onChange={e => setQuantityMethod(e.target.checked ? 'auto' : 'manual')}
            value='auto'
          />
          すべてを決済
        </label>
      </div>
      <div className="form-check ms-2">
        <label className="form-check-label">
          <input className="form-check-input" type="radio"
            checked={quantityMethod === 'manual'}
            onChange={e => setQuantityMethod(e.target.checked ? 'manual' : 'auto')}
            value='manual'
          />
          数量を手動指定
        </label>
      </div>
    </div>
    {(instrument && quantityMethod === 'auto') && <>
      <p className="text-095rem mb-1">同じ戦略で保有中の、すべてのポジションが決済されます。</p>
    </>}
    {(instrument && quantityMethod === 'manual') && <>
      <label className="mt-1">手動指定数量（{instrument.tradingLot}単位）</label>
      <input type="number" className="form-control"
        step={instrument.tradingLot}
        min={instrument.tradingLot}
        value={quantity || ''}
        onChange={e => setQuantity(parseInt(e.target.value))}
      />
    </>}
    {quote && <>
      <div className="d-flex justify-content-between align-items-end">
        <label className="mt-1">現在価格</label>
        <small>{getTimeInJST(quote?.currentPriceAt)}更新</small>
      </div>
      <div className="input-group">
        <input type="text" className="form-control" value={quote?.currentPrice || "取得中..."} disabled />
        <button className="btn btn-outline-secondary manual-order-modal__quote-button" onClick={updateQuote}>
          更新
        </button>
      </div>
    </>}

    <form action="/user/manual_exit_orders" method="post" ref={formRef}>
      <input type="hidden" name="authenticity_token" value={Rails.csrfToken() || ''} />
      <input type="hidden" name="subscription_position_uid" value={subscriptionPosition.uid || ''} />
      {quantityMethod === 'manual' && (
        <input type="hidden" name="quantity" value={quantity || ''} />
      )}

      <div className="d-grid mt-3">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!canSubmit}
          onClick={(e)=> {
            e.preventDefault();
            setIsSubmitting(true);
            formRef.current?.submit();
          }}
        >
          決済を実行する
        </button>
      </div>
    </form>
  </>);
}

export default ManualExitOrderForm;